import EventEmitter, { EVENT_TYPES } from '@lib/eventEmitter';
import ShortlistButton from '@components/shortlist/shortlistButton';
import ImageWrapper from './imageWrapper';
import ImageWrapperExperiment from './imageWrapperExperiment';
import VideoLinks from '@javascript/vehicle_product/videoLinks';
import ImageGalleryButton from './imageGalleryButton';
import SummaryChips from './summaryChips';
import classNames from 'classnames';
import useTitleWrapDetection from './useTitleWrapDetection';
import type { WithExperiments } from '@javascript/types/Experiment';
import type { EventType } from '@sharedTypes/InstantSearch';
import type { SearchVehicle } from '@javascript/types/Vehicle';
import { Vehicle } from '@sharedTypes/Vehicle';
import BranchFooter from './branchFooter';
import StockAlertLauncher from '@components/stock_alerts/stockAlertLauncher';
import { setAlgoliaAttributedClick } from '@javascript/vehicle_product/instant_search/algoliaAttributedClick';
import { createStockAlertUrlFromVehicle } from '@javascript/stock_alerts/helpers';
import SearchPricing from './searchPricing';
import ReadyToAdvertiseChip from '@components/application/vehicle/readyToAdvertiseChip';
import StatusBanner from '@components/application/vehicle/statusBanner';

interface vehicleResultProps {
  make: string;
  model: string;
  salesInfo: SearchVehicle['salesInfo'];
  stockReference: string;
  title: { name: string; variant: string };
  url: string;
  variant: string;
  algoliaQueryID?: string;
  analytics?: () => void;
  authenticityToken?: string;
  branch?: Vehicle['branch'];
  children?: React.ReactNode;
  clickAndCollectDetails?: Vehicle['clickAndCollectDetails'];
  colour?: string[];
  compareButton?: React.ReactNode;
  hideImageSlider?: boolean;
  imageCount?: number;
  index?: number;
  instantSearchAnalytics?: (event: EventType, label: string) => void;
  isAwaitingImages?: boolean;
  isMovable?: boolean;
  isPhyronAvailable?: boolean;
  isReserved?: boolean;
  isShowcaseContent?: boolean;
  photos?: string[];
  readyToAdvertise?: boolean;
  referrer?: string;
  thumbnails?: string[];
}

const openQuickGallery = (stockReference: string) => {
  EventEmitter.emit(EVENT_TYPES.openModal, 'imageGallery', {
    reference: stockReference,
    dmsid: null,
    originPage: 'Search results',
  });
};

const VehicleResult = ({
  algoliaQueryID,
  analytics,
  branch,
  children,
  clickAndCollectDetails,
  colour,
  compareButton,
  experiments,
  hideImageSlider,
  imageCount,
  index,
  instantSearchAnalytics,
  isAwaitingImages,
  isMovable,
  isPhyronAvailable,
  isReserved,
  isShowcaseContent,
  make,
  model,
  photos,
  readyToAdvertise,
  referrer,
  salesInfo,
  stockReference,
  thumbnails,
  title,
  url,
  variant,
}: vehicleResultProps & WithExperiments) => {
  const { isTitleNameWrapped, titleNameRef } = useTitleWrapDetection(
    !!isShowcaseContent,
    title.name,
  );

  const handleClick = () => {
    if (algoliaQueryID) {
      sessionStorage.removeItem('trackedEvents');
      setAlgoliaAttributedClick({
        objectID: stockReference,
        queryID: algoliaQueryID,
      });
    }

    if (referrer !== 'product-similarVehicles') return false;
  };

  const handleAnalytics = () => {
    if (analytics) {
      analytics();
    }
    if (instantSearchAnalytics) {
      instantSearchAnalytics('click', 'Clicks a search result');
    }
  };

  const stockAlertUrl = createStockAlertUrlFromVehicle({
    colour,
    make,
    model,
    variant,
  });

  return (
    <article
      className="ch-card ac-result ch-rounded--lg ch-ba--0 ch-shadow--xs"
      onClick={handleClick}
    >
      <div className="ac-result__chip-wrapper">{children}</div>
      <ShortlistButton
        instantSearchAnalytics={instantSearchAnalytics}
        stockReference={stockReference}
      />
      {readyToAdvertise && !isReserved ? (
        <ReadyToAdvertiseChip />
      ) : (
        <StatusBanner isAwaitingImages={isAwaitingImages} />
      )}
      <div className="ch-relative">
        <a
          aria-label={`View ${title.name} ${title.variant}`.trim()}
          className="ch-display--block"
          href={url}
          {...((instantSearchAnalytics || analytics) && {
            onClick: handleAnalytics,
          })}
        >
          {experiments?.lazy ? (
            <ImageWrapperExperiment
              hideImageSlider={hideImageSlider}
              imageCount={imageCount}
              photos={experiments?.thumbnails ? thumbnails : photos}
              resultIndex={index}
            />
          ) : (
            <ImageWrapper
              hideImageSlider={hideImageSlider}
              imageCount={imageCount}
              photos={experiments?.thumbnails ? thumbnails : photos}
            />
          )}
        </a>
        <ImageGalleryButton
          imageCount={imageCount}
          onClick={() => {
            if (instantSearchAnalytics) {
              instantSearchAnalytics('click', 'Opens image gallery');
            }

            openQuickGallery(stockReference);
          }}
        />
        {!!compareButton && compareButton}
        {isPhyronAvailable && (
          <VideoLinks
            instantSearchAnalytics={instantSearchAnalytics}
            isPhyronAvailable={isPhyronAvailable}
            stockReference={stockReference}
          />
        )}
      </div>
      <div
        className={classNames(
          'ch-display--flex ch-flex-direction--column ch-align-items--stretch ch-justify-content--center',
          isShowcaseContent && isTitleNameWrapped ? 'ch-pv--2' : 'ch-pv--3',
        )}
      >
        {isReserved && (
          <span className="ch-mh--2 ch-mb--2 ch-ph--2 ch-fw--500 ac-result__pill ac-result__pill--reserved">
            Reserved
          </span>
        )}
        <h2 className="ac-vehicle__title ch-fw--500 ch-fs--4 ch-mb--2 ch-mh--2 ch-display--flex ch-align-items--center ch-overflow--hidden">
          <a
            className="ch-text-decoration--none ch-display--block ch-width--12"
            href={url}
            {...(instantSearchAnalytics && {
              onClick: () =>
                instantSearchAnalytics('click', 'Clicks a search result'),
            })}
          >
            <span ref={isShowcaseContent ? titleNameRef : undefined}>
              {title.name}
            </span>
            <small className="ac-vehicle__title--variant ch-text--ellipsis ch-overflow--hidden ch-display--block ch-fw--400 ch-fs--3">
              {title.variant}
            </small>
          </a>
        </h2>
        <SummaryChips summary={salesInfo.summary} />
      </div>
      {!isReserved && (
        <SearchPricing
          pricing={salesInfo.pricing}
          stockReference={stockReference}
          useShowcasePricingPadding={isShowcaseContent}
          vehicleUrl={url}
        />
      )}
      {isReserved && (
        <div className="ch-bg--grey-1 ch-mh--2 ch-pa--1 ch-fs--2 ch-mb--3">
          <p className="ac-result__reserved-message ch-color--grey-6">
            This car is currently reserved, but we may have a similar one in
            stock soon.
          </p>
          <StockAlertLauncher
            additionalStyling="ch-btn ch-btn--link ch-pl--0 ch-pr--0 ch-fs--2 ch-fw--500"
            analyticsLocation="Vehicle card reserved"
            stockAlertUrl={stockAlertUrl}
          />
        </div>
      )}
      <BranchFooter
        branchInfo={branch}
        clickAndCollectDetails={clickAndCollectDetails}
        vehicleMovable={isMovable}
      />
    </article>
  );
};

export default VehicleResult;
